import {
  Money,
  TransferOptionsResult,
  TransferOptionsResultTypenames,
  TransferRouteDetails,
  TransferRouteDetailsResult,
  TransferRouteDetailsResultTypenames,
} from '@varo/graphql-schema-types';
import { moneyAmountToMinorUnits } from 'features/feeBasedInstantTransfer/helpers/transferAmount';
import type {
  ReadyTransferRoutePolicy,
  TransferPrincipalLimit,
} from 'features/feeBasedInstantTransfer/hooks/useSelectedRoutePolicy';
import { toTagIconKey } from 'shared/components/vxl/Tag/Tag.helpers';

type TransferRoutePolicyFailureReason = 'transferNotPossible' | 'requestFailed';

interface AdaptedUnavailableRoutePolicy {
  status: 'unavailable';
  reason: TransferRoutePolicyFailureReason;
  message?: string;
}

type AdaptedPart<Ready> =
  ({ status: 'ready' } & Ready) | AdaptedUnavailableRoutePolicy;

type RouteSpeed = Pick<
  ReadyTransferRoutePolicy,
  'speedDescription' | 'speedIcon'
>;

export interface RoutePolicyResolution {
  policy: ReadyTransferRoutePolicy | AdaptedUnavailableRoutePolicy;
  hasRouteDetailsFailed: boolean;
  hasMaximumFailed: boolean;
}

interface RouteDetailsQueryOutcome {
  result: TransferRouteDetailsResult | null | undefined;
  hasRequestFailed: boolean;
}

interface MaximumQueryOutcome {
  result: TransferOptionsResult | null | undefined;
  hasRequestFailed: boolean;
}

const REQUEST_FAILED: AdaptedUnavailableRoutePolicy = {
  status: 'unavailable',
  reason: 'requestFailed',
};

const hasRequestFailed = (part: AdaptedPart<object>) =>
  part.status === 'unavailable' && part.reason === 'requestFailed';

const adaptTransferNotPossible = (
  message: string,
): AdaptedUnavailableRoutePolicy => ({
  status: 'unavailable',
  reason: 'transferNotPossible',
  message,
});

/**
 * Converts a server Money value into an exact principal limit. Returns
 * undefined when the money is missing or its amount cannot be represented as
 * exact integer minor units.
 */
export function adaptPrincipalLimit(
  money: Money | null | undefined,
): TransferPrincipalLimit | undefined {
  const minorUnits = money ? moneyAmountToMinorUnits(money.amount) : undefined;
  return money && minorUnits !== undefined
    ? { minorUnits, formatted: money.formatted }
    : undefined;
}

function adaptRouteDetails({
  result,
  hasRequestFailed,
}: RouteDetailsQueryOutcome): AdaptedPart<{
  routeDetails?: TransferRouteDetails;
}> {
  if (hasRequestFailed) {
    return REQUEST_FAILED;
  }
  if (
    result?.__typename ===
    TransferRouteDetailsResultTypenames.TransferNotPossibleError
  ) {
    return adaptTransferNotPossible(result.message);
  }
  return {
    status: 'ready',
    routeDetails:
      result?.__typename ===
      TransferRouteDetailsResultTypenames.TransferRouteDetails
        ? result
        : undefined,
  };
}

function adaptRouteSpeed(
  routeDetails: TransferRouteDetails | undefined,
): RouteSpeed {
  if (!routeDetails) {
    return {};
  }
  const { speedDescription, icon } = routeDetails.transferSpeed;
  return {
    speedDescription:
      speedDescription.trim() === '' ? undefined : speedDescription,
    speedIcon: toTagIconKey(icon) ?? undefined,
  };
}

function adaptAccountMaximumPrincipal({
  result,
  hasRequestFailed,
}: MaximumQueryOutcome): AdaptedPart<{
  maximumPrincipal?: TransferPrincipalLimit;
}> {
  if (hasRequestFailed) {
    return REQUEST_FAILED;
  }
  if (
    result?.__typename ===
    TransferOptionsResultTypenames.TransferNotPossibleError
  ) {
    return adaptTransferNotPossible(result.message);
  }
  return {
    status: 'ready',
    maximumPrincipal:
      result?.__typename === TransferOptionsResultTypenames.TransferOptions
        ? adaptPrincipalLimit(result.maxTransfer)
        : undefined,
  };
}

function toNonEmptyMessage(message: string | null | undefined) {
  return message?.trim() ? message : undefined;
}

/**
 * Resolves the policy for an external debit card destination. The principal
 * limits and their messages come from the server's fee info, when present,
 * and are passed through verbatim.
 */
export function resolveCardRoutePolicy(
  routeDetailsOutcome: RouteDetailsQueryOutcome,
): RoutePolicyResolution {
  const adapted = adaptRouteDetails(routeDetailsOutcome);
  let policy: RoutePolicyResolution['policy'];
  if (adapted.status === 'unavailable') {
    policy = adapted;
  } else {
    const { routeDetails } = adapted;
    const feeInfo = routeDetails?.feeInfo;
    policy = {
      status: 'ready',
      ...adaptRouteSpeed(routeDetails),
      feePercentageText: feeInfo?.feePercentageText,
      minimumPrincipal: adaptPrincipalLimit(feeInfo?.minimumPrincipalAmount),
      maximumPrincipal: adaptPrincipalLimit(feeInfo?.maximumPrincipalAmount),
      minimumMessage: toNonEmptyMessage(feeInfo?.minimumPrincipalMessage),
      maximumMessage: toNonEmptyMessage(feeInfo?.maximumPrincipalMessage),
      limitErrorMessage: toNonEmptyMessage(feeInfo?.limitErrorMessage),
    };
  }

  return {
    policy,
    hasRouteDetailsFailed: hasRequestFailed(policy),
    hasMaximumFailed: false,
  };
}

/**
 * Resolves the policy for an account destination. Route details supply the
 * speed; the transfer maximum query supplies the per-transaction maximum.
 * Unavailable when either query is, preferring the route details failure.
 */
export function resolveAccountRoutePolicy(
  routeDetailsOutcome: RouteDetailsQueryOutcome,
  maximumOutcome: MaximumQueryOutcome,
): RoutePolicyResolution {
  const routeDetails = adaptRouteDetails(routeDetailsOutcome);
  const maximum = adaptAccountMaximumPrincipal(maximumOutcome);
  let policy: RoutePolicyResolution['policy'];
  if (routeDetails.status === 'unavailable') {
    policy = routeDetails;
  } else if (maximum.status === 'unavailable') {
    policy = maximum;
  } else {
    policy = {
      status: 'ready',
      ...adaptRouteSpeed(routeDetails.routeDetails),
      maximumPrincipal: maximum.maximumPrincipal,
    };
  }

  return {
    policy,
    hasRouteDetailsFailed: hasRequestFailed(routeDetails),
    hasMaximumFailed: hasRequestFailed(maximum),
  };
}
